import React, { useState } from "react";
import {
  MDBDropdown,
  MDBDropdownToggle,
  MDBDropdownMenu,
  MDBDropdownItem,
  MDBBreadcrumb,
  MDBBreadcrumbItem,
  MDBIcon,
} from "mdbreact";
import {
  DocumentIcon,
  DownArrowIcon,
  DatabaseIcon,
  SaveIcon,
  UploadIcon,
  EllipsesIcon,
  WalletIcon,
  RullerIcon,
  ProfileIcon,
  ProjectIcon,
} from "assets/svg-icons";
import { SCOPE_COMPANY, SCOPE_BIDLIGHT, toolbarOptions } from "helpers/estimatingDatabase/databaseRows";
import { locationNames, typeDisplayName } from "./lookups";

const show = (v) => (v && v !== "null" ? v : null);

// The version / scope / action bar, laid out like the Pricing Catalogs
// filtration bar. Every action is a callback; the page decides what it does.
export const DatabaseFiltrationBar = ({
  workspace,
  pending,
  onAdd,
  onEdit,
  onDelete,
  onClone,
  onSave,
  onSaveToVersion,
  onSaveToDefault,
  onExport,
  onImport,
  onLoad,
  onPickVersion,
  onNewVersion,
  confirmLeave,
  addLabel,
  disableAdd,
}) => {
  const { templates, template, setTemplate, scope, setScope, isAdmin, projectTypes, laborTypes, countries } = workspace;
  const [templateSearch, setTemplateSearch] = useState("");
  const bidlight = scope === SCOPE_BIDLIGHT;
  // DatabaseTabPage.xaml + DatabaseTabViewModel.ShowEditDeleteOption /
  // ShowAddCloneOption: the BidLight (template) scope hides add, clone,
  // edit, delete and import; a BidLight admin in the company scope gets
  // edit and delete but not add or clone.
  const { showEditDelete, showAddClone, showImport } = toolbarOptions({ bidlight, isAdmin });

  const filteredTemplates = (templates || []).filter((t) =>
    String(t.Code || "").toLowerCase().includes(templateSearch.toLowerCase())
  );
  // The version stores codes (Iso3, StateCode, the type's APIUrlIdentifier);
  // the desktop shows the matching item's name and leaves a field empty when
  // nothing matches.
  const place = locationNames(countries, template);
  const breadcrumb = [place.country, place.state, place.city].filter(Boolean);
  const laborTypeName = typeDisplayName(laborTypes, template && template.LaborType);
  const projectTypeName = typeDisplayName(projectTypes, template && template.ProjectType);

  const scopeRadio = (value, label) => (
    <label className="d-flex align-items-center gap-2 mb-0 cursor-pointer" style={{ whiteSpace: "nowrap" }}>
      <input
        type="radio"
        name="database-scope"
        checked={scope === value}
        onChange={() => {
          if (scope !== value) confirmLeave(() => setScope(value));
        }}
      />
      {label}
    </label>
  );

  return (
    <div className="d-flex flex-row justify-center gap-2 px-4 bg-white flex-wrap">
      <div
        className="d-flex flex-column p-3"
        style={{ border: "1px solid #E2E2E2", borderRadius: "9px", flex: "1 1 600px" }}
      >
        <div className="d-flex flex-row gap-3 flex-wrap">
          <MDBDropdown dropend group className="dropdownList position-relative" style={{ minWidth: 220 }}>
            <span className="position-absolute bottom-2 lable">Template</span>
            <MDBDropdownToggle className="pricingDropdown d-flex justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <DocumentIcon />
                <span className="dropdownItems">{(template && template.Code) || "Templates"}</span>
              </div>
              <div>
                <DownArrowIcon />
              </div>
            </MDBDropdownToggle>
            <MDBDropdownMenu className="w-100 border-none" responsive="end">
              <div className="px-2">
                <input
                  onChange={(e) => setTemplateSearch(e.target.value)}
                  type="text"
                  className="form-control p-2"
                  placeholder="Search Template"
                />
              </div>
              {onNewVersion && (
                <MDBDropdownItem className="optionItems" onClick={() => confirmLeave(onNewVersion)} data-testid="database-new-version">
                  + New version
                </MDBDropdownItem>
              )}
              <div className="list">
                {filteredTemplates.map((t, i) => (
                  <MDBDropdownItem
                    className="optionItems"
                    key={`${t._id || t.Code}_${i}`}
                    title={t.Code}
                    onClick={() => confirmLeave(() => (onPickVersion ? onPickVersion(t) : setTemplate(t)))}
                  >
                    {t.Code}
                  </MDBDropdownItem>
                ))}
              </div>
            </MDBDropdownMenu>
          </MDBDropdown>
          <div className="d-flex align-items-center gap-2 f-14">
            <ProfileIcon />
            <span data-testid="database-labor-type">{laborTypeName || "Labor type"}</span>
          </div>
          <div className="d-flex align-items-center gap-2 f-14">
            <ProjectIcon />
            <span data-testid="database-project-type">{projectTypeName || "Project Type"}</span>
          </div>
          <div className="d-flex align-items-center gap-2 f-14">
            <WalletIcon />
            {show(template && template.Currency) || "Currency"}
          </div>
          <div className="d-flex align-items-center gap-2 f-14">
            <RullerIcon />
            {show(template && template.Measurements) || "Units"}
          </div>
        </div>

        <div className="mt-2">
          <MDBBreadcrumb className="breadcrumb d-flex justify-content-between flex-wrap">
            <div className="d-flex">
              <div className="mr-3">
                <DatabaseIcon color="black" />
              </div>
              {breadcrumb.map((item, index) => (
                <MDBBreadcrumbItem className="breadcrumb-item" key={`${item}_${index}`} data-testid="database-breadcrumb">
                  <span>{item}</span>
                </MDBBreadcrumbItem>
              ))}
            </div>
            <div className="d-flex gap-3 justify-content-center align-items-center flex-wrap">
              {scopeRadio(SCOPE_COMPANY, "Company database")}
              {scopeRadio(SCOPE_BIDLIGHT, "BidLight database")}
              {(showEditDelete || showAddClone) && <div style={{ border: "1px solid #D0D5DD", height: "20px" }} />}
              {showEditDelete && (
                <span
                  style={{ cursor: "pointer" }}
                  title="Edit selected"
                  onClick={onEdit}
                  data-testid="database-edit"
                >
                  <MDBIcon fas icon="edit" />
                </span>
              )}
              {showEditDelete && (
                <span
                  style={{ cursor: "pointer" }}
                  title="Delete selected"
                  onClick={onDelete}
                  data-testid="database-delete"
                >
                  <MDBIcon fas icon="trash-alt" />
                </span>
              )}
              {showAddClone && onClone && (
                <span
                  style={{ cursor: disableAdd ? "not-allowed" : "pointer", opacity: disableAdd ? 0.4 : 1 }}
                  title="Clone selected"
                  onClick={() => !disableAdd && onClone()}
                  data-testid="database-clone"
                >
                  <MDBIcon far icon="copy" />
                </span>
              )}
              {showAddClone && (
                <span
                  style={{ cursor: disableAdd ? "not-allowed" : "pointer", opacity: disableAdd ? 0.4 : 1 }}
                  title={addLabel || "Add"}
                  onClick={() => !disableAdd && onAdd()}
                  data-testid="database-add"
                >
                  <MDBIcon fas icon="plus" />
                </span>
              )}
            </div>
          </MDBBreadcrumb>
        </div>
      </div>

      <div className="d-flex justify-content-center align-items-center" style={{ flex: "0 1 320px" }}>
        <div
          className="d-flex flex-column gap-3 p-3"
          style={{ border: "1px solid #E2E2E2", borderRadius: "9px", width: "100%", height: "fit-content" }}
        >
          <div className="f-14">
            {bidlight
              ? "Tick rows and save to copy them into the company database."
              : pending
              ? `${pending} unsaved change${pending > 1 ? "s" : ""}`
              : "No unsaved changes"}
          </div>
          <div className="d-flex flex-row gap-2">
            <MDBDropdown className="border-none catalogbtn">
              <MDBDropdownToggle
                className={`bg-bid d-flex flex-row justify-content-between ${!template && "disabled"}`}
              >
                <div className="d-flex gap-2">
                  <SaveIcon />
                  Save
                </div>
                <EllipsesIcon />
              </MDBDropdownToggle>
              <MDBDropdownMenu className="w-100 border-none" responsive="end">
                <MDBDropdownItem onClick={onSave}>
                  {bidlight ? "Move selection to company database" : "Save to database"}
                </MDBDropdownItem>
                <MDBDropdownItem onClick={onSaveToVersion}>Save to other version</MDBDropdownItem>
                {isAdmin && (
                  <MDBDropdownItem onClick={onSaveToDefault}>Save to BidLight default</MDBDropdownItem>
                )}
                <MDBDropdownItem onClick={onExport}>Save to Excel</MDBDropdownItem>
              </MDBDropdownMenu>
            </MDBDropdown>
            <MDBDropdown className="border-none catalogbtn">
              <MDBDropdownToggle
                className={`bg-bid d-flex flex-row justify-content-between ${!template && "disabled"}`}
              >
                <div className="d-flex gap-2">
                  <UploadIcon />
                  Load
                </div>
                <EllipsesIcon />
              </MDBDropdownToggle>
              <MDBDropdownMenu className="w-100 border-none" responsive="end">
                <MDBDropdownItem className="optionItem" onClick={() => confirmLeave(onLoad)}>
                  Load from database
                </MDBDropdownItem>
                {showImport && (
                  <MDBDropdownItem className="optionItem" onClick={onImport}>
                    Load Excel
                  </MDBDropdownItem>
                )}
              </MDBDropdownMenu>
            </MDBDropdown>
          </div>
        </div>
      </div>
    </div>
  );
};
